/* eslint-disable react/no-danger */
import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Leader from '../partials/Leader';
import Rank from '../partials/Rank';

import styles from '../../../styles/steps/end.module.scss';

const propTypes = {
    step: PropTypes.shape({}),
    game: PropTypes.shape({}),
    leaderboard: PropTypes.shape({}),
    isDisplay: PropTypes.bool,
    isPreview: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    step: null,
    game: null,
    leaderboard: null,
    isDisplay: false,
    isPreview: false,
    className: null,
};

function End({ step, game, leaderboard, isDisplay, isPreview, className }) {
    const { name: theme = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const user = useUser();
    const { id = null, name = null } = user || {};
    const {
        title = null,
        titleWinner = null,
        titleLoser = null,
        descriptionWinner = null,
        descriptionLoser = null,
    } = step || {};

    const { leaders = [] } = leaderboard || {};
    const { title: gameTitle = null, order = null } = game || {};

    const userLeader = (leaders || []).find((l) => id !== null && l.id === id) || null;
    const userPosition = (leaders || []).findIndex((l) => id !== null && l.id === id);
    const finalLeaders = isDisplay ? (leaders || []).slice(0, 10) : leaders || [];
    const [firstLeader = null, secondLeader = null] = finalLeaders || [];
    const { points: firstPoints = null } = firstLeader || {};
    const { points: secondPoints = null } = secondLeader || {};
    const pointsBump = firstPoints !== null && firstPoints === secondPoints ? 10 : 0;

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                { [styles.isDisplay]: isDisplay, [className]: className !== null },
            ])}
        >
            <div className={styles.inner}>
                {name !== null && userLeader !== null && !isDisplay && !isPreview ? (
                    <div className={styles.rankings}>
                        {userPosition !== null && userPosition !== -1 ? (
                            <Rank
                                className={styles.medal}
                                user={user}
                                leader={userLeader}
                                position={userPosition}
                            />
                        ) : null}
                        {userPosition === 0 ? (
                            <>
                                <h3 className={styles.rankTitle}>
                                    {titleWinner || (
                                        <FormattedMessage
                                            defaultMessage="Victory!"
                                            description="Description"
                                        />
                                    )}
                                </h3>
                                {descriptionWinner !== null ? (
                                    <div
                                        className={styles.rankDescription}
                                        dangerouslySetInnerHTML={{ __html: descriptionWinner }}
                                    />
                                ) : (
                                    <div className={styles.rankDescription}>
                                        <p>
                                            <FormattedMessage
                                                defaultMessage="Thank you for playing!"
                                                description="Step description"
                                            />
                                        </p>
                                        <p>
                                            <FormattedMessage
                                                defaultMessage="Go see the quizmaster to get your prize."
                                                description="Step description"
                                            />
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : null}
                        {userPosition > 0 ? (
                            <>
                                <h3 className={styles.rankTitle}>
                                    {titleLoser || (
                                        <FormattedMessage
                                            defaultMessage="Well played!"
                                            description="Description"
                                        />
                                    )}
                                </h3>
                                {descriptionLoser !== null ? (
                                    <div
                                        className={styles.rankDescription}
                                        dangerouslySetInnerHTML={{ __html: descriptionLoser }}
                                    />
                                ) : (
                                    <div className={styles.rankDescription}>
                                        <p>
                                            <FormattedMessage
                                                defaultMessage="Thank you for playing!"
                                                description="Step description"
                                            />
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : null}
                        {theme === 'perfect' ? (
                            <a
                                href={`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent('#ThePerfectScore #MolsonCoors')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.link}
                            >
                                <FormattedMessage
                                    defaultMessage="Share on LinkedIn"
                                    description="Link label"
                                />
                            </a>
                        ) : null}
                    </div>
                ) : null}
                <h1 className={styles.title}>
                    {title || (
                        <FormattedMessage
                            defaultMessage="Final leaderboard"
                            description="Title label"
                        />
                    )}
                </h1>
                <h2 className={styles.subtitle}>
                    {theme === 'defis' ? (
                        <>
                            <FormattedMessage
                                defaultMessage="Challenge {count}:"
                                description="Title label"
                                values={{ count: order }}
                            />{' '}
                        </>
                    ) : null}
                    {gameTitle}
                </h2>
                {isDisplay ? (
                    <p className={styles.about}>
                        <FormattedMessage
                            defaultMessage="Thank you for playing!"
                            description="Title label"
                        />
                    </p>
                ) : null}
                <div className={styles.leaders}>
                    {finalLeaders.map((leader, index) => (
                        <Leader
                            key={`leader-${leader.id}`}
                            className={styles.leader}
                            user={user}
                            position={String(index + 1).padStart(2, '0')}
                            leader={{
                                ...leader,
                                points: index === 0 ? leader.points + pointsBump : leader.points,
                            }}
                        />
                    ))}
                </div>
                {leaders === null || leaders.length === 0 ? (
                    <p className={styles.empty}>
                        <FormattedMessage
                            defaultMessage="No good answer!"
                            description="Display message"
                        />
                    </p>
                ) : null}
            </div>
        </div>
    );
}

End.propTypes = propTypes;
End.defaultProps = defaultProps;

export default End;
